import { Markdown } from '@/components/Markdown';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { useState } from 'react';
import { X } from 'lucide-react';

interface Citation {
  title?: string;
  content?: string;
  url?: string;
  marker?: string;
  index?: number;
}

interface SourcesAccordionProps {
  sources: string[];
  citations?: Citation[];
}

const SourcesAccordion = ({ sources, citations = [] }: SourcesAccordionProps) => {
  const [selectedCitation, setSelectedCitation] = useState<Citation | null>(null);

  if (!sources || sources.length === 0) {
    return null;
  }

  const handleSourceClick = (index: number) => {
    if (citations && citations[index]) {
      setSelectedCitation(citations[index]);
    }
  };

  const handleCloseSidePanel = () => {
    setSelectedCitation(null);
  };

  // Helper to convert number to superscript
  const toSuperscript = (num: number): string => {
    const superscriptMap: { [key: string]: string } = {
      '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴',
      '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹'
    };
    return num.toString().split('').map(d => superscriptMap[d] || d).join('');
  };

  // If no structured citations, fall back to rendering as markdown list
  if (!citations || citations.length === 0) {
    const normalized = sources
      .map((source) => source.trim())
      .filter(Boolean)
      .map((source) => {
        if (source.startsWith('- ')) {
          return source;
        }
        return `- ${source}`;
      });

    const sourcesMarkdown = normalized.join('\n');

    return (
      <div className="mt-4 pt-4 border-t">
        <Accordion type="single" collapsible defaultValue="sources">
          <AccordionItem value="sources">
            <AccordionTrigger className="text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                Sources ({sources.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-1 text-sm">
                <Markdown>
                  {sourcesMarkdown}
                </Markdown>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  }

  return (
    <>
      <div className="mt-4 pt-4 border-t">
        <Accordion type="single" collapsible defaultValue="sources">
          <AccordionItem value="sources">
            <AccordionTrigger className="text-sm font-medium hover:no-underline">
              <span className="flex items-center gap-2">
                Sources ({sources.length})
              </span>
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2 text-sm">
                {citations.map((citation, index) => {
                  const citationNumber = citation.index || (index + 1);
                  const superscriptNum = toSuperscript(citationNumber);
                  
                  return (
                    <button
                      key={index}
                      onClick={() => handleSourceClick(index)}
                      className="w-full text-left px-3 py-2 rounded hover:bg-accent transition-colors flex items-start gap-2 group"
                    >
                      <span className="text-muted-foreground mt-0.5 font-medium">{superscriptNum}</span>
                      <span className="flex-1 underline" style={{ color: '#0000EE' }}>
                        {citation.title || `Source ${citationNumber}`}
                      </span>
                    </button>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Side Panel for Citation Content */}
      {selectedCitation && (
        <div className="fixed inset-y-0 inset-x-0 sm:inset-x-auto sm:right-0 w-full sm:w-96 max-w-full sm:max-w-96 bg-background border-l shadow-lg z-50 flex flex-col source-details-panel">
          <div className="flex items-center justify-between p-4 border-b">
            <h3 className="font-semibold text-lg break-words flex-1 mr-2">Source Details</h3>
            <button
              onClick={handleCloseSidePanel}
              className="p-1 rounded hover:bg-accent transition-colors flex-shrink-0"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {selectedCitation.title && (
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-1">Title</h4>
                <p className="text-sm break-words hyphens-auto">{selectedCitation.title}</p>
              </div>
            )}
            {selectedCitation.content && (
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-2">Content</h4>
                <div className="text-sm prose prose-sm max-w-none dark:prose-invert whitespace-pre-wrap break-words overflow-wrap-anywhere hyphens-auto">
                  <Markdown>{selectedCitation.content}</Markdown>
                </div>
              </div>
            )}
            {selectedCitation.url && (
              <div>
                <h4 className="font-medium text-sm text-muted-foreground mb-1">Document SharePoint Link:</h4>
                <a 
                  href={selectedCitation.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:text-blue-800 underline break-all"
                  style={{ wordBreak: 'break-all', overflowWrap: 'anywhere' }}
                >
                  {selectedCitation.url}
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Backdrop for side panel */}
      {selectedCitation && (
        <div
          className="fixed inset-0 bg-black/20 z-40 md:bg-transparent"
          onClick={handleCloseSidePanel}
        />
      )}
    </>
  );
};

export { SourcesAccordion };
